import React, { Component } from 'react';
import { bool, func, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { getBuyerId } from 'dibs-cookie-jar';
import get from 'lodash.get';

import MessageBanner from '../MessageBanner/MessageBanner';
import TopBar from '../TopBar/TopBar';
import BottomBar from '../BottomBar/BottomBar';
import Drawer from '../Drawer/Drawer';

import { actionCreators } from '../../actions';
import * as userTracking from '../../utils/userTracking';
import chat from '../../utils/chat';
import {
    getCurrencyPreference,
    setSessionCurrency,
    setLocalCurrency,
    getSessionCurrency,
} from '../../utils/preferredCurrency';
import styles from './Header-style.scss';
import { hasValidLocalUserGeoInfo, setLocalUserGeoInfo } from '../../utils/userGeoInfo';

class Header extends Component {
    constructor(props) {
        super(props);

        this.state = {
            hasError: false,
            forceSlideIn: false,

            fragmentVariables: {
                isClient: false,
                hasUserId: false,
                showMarketingMessages: this.props.showMarketingMessages,
                userId: '',
                userIds: [],
                fetchRegionalInfo: false,
            },
        };

        this.handleTopBarMouseOver = this.handleTopBarMouseOver.bind(this);
        this.setupRegionalInfo = this.setupRegionalInfo.bind(this);
    }

    componentDidMount() {
        this.refetchViewer();
    }

    componentDidUpdate(prevProps) {
        if (this.props.updateUserState && !prevProps.updateUserState) {
            this.refetchViewer();
        }

        if (get(this.props.viewer, 'user.isVerifiedTrade') && !get(prevProps.viewer, 'user')) {
            this.props.updateUserTypeTrade();
        }
        if (get(this.props.viewer, 'user.isVip') && !get(prevProps.viewer, 'user')) {
            this.props.updateUserTypeVip();
        }

        if (this.state.fragmentVariables.isClient && !this.props.initialTrackingFired) {
            const { viewer } = this.props;
            const userId = getBuyerId(document.cookie);
            const hasUserId = !!userId;
            const hasUser = !!get(viewer, 'user');

            if (!hasUserId) {
                userTracking.init({ viewer });
                this.props.setInitialTrackingFired();
            } else if (hasUser) {
                const { user } = viewer;
                chat.fillUserData(viewer, user);
                userTracking.init({ viewer, user });
                this.props.setInitialTrackingFired();
            }
        }
    }

    componentDidCatch(error) {
        if (window.newrelic) {
            const newrelicError = new Error();
            newrelicError.message = `dibs-buyer-layout header error boundary triggered: ${
                error.message
            }`;
            newrelicError.stack = error.stack;

            window.newrelic.noticeError(newrelicError);
        }

        this.setState({ hasError: true });
    }

    getClassnames({ forceDisableStickyNav, hasTopBarOnly }) {
        const { disableStickyNav } = this.props;

        return {
            wrapperClasses: classnames({
                [styles.wrapper]: true,
            }),
            headerClasses: classnames({
                [styles.header]: true,
                [styles.disableSticky]: disableStickyNav || forceDisableStickyNav,
                [styles.hasTopBarOnly]: hasTopBarOnly,
            }),
            shadowClasses: classnames({
                [styles.shadow]: true,
                [styles.disableSticky]: disableStickyNav || forceDisableStickyNav,
                [styles.hasTopBarOnly]: hasTopBarOnly,
            }),
            bottomWrapperClasses: classnames({
                [styles.bottomWrapper]: true,
                [styles.disableSticky]: disableStickyNav || forceDisableStickyNav,
            }),
        };
    }

    refetchViewer() {
        const { viewer } = this.props;
        const userId = getBuyerId(document.cookie) || '';
        const hasUserId = !!userId;
        const fetchRegionalInfo =
            (!hasUserId && !getSessionCurrency()) || !hasValidLocalUserGeoInfo(viewer);
        this.setState(
            {
                fragmentVariables: {
                    ...this.state.fragmentVariables,
                    isClient: true,
                    hasUserId,
                    userId,
                    userIds: [userId],
                    fetchRegionalInfo,
                },
            },
            () => {
                if (
                    this.state.fragmentVariables.hasUserId ||
                    this.state.fragmentVariables.fetchRegionalInfo
                ) {
                    this.props.relay.refetch(
                        this.state.fragmentVariables,
                        null,
                        this.setupRegionalInfo
                    );
                } else {
                    this.setupRegionalInfo();
                }
            }
        );
    }
    setupRegionalInfo() {
        const { viewer } = this.props;

        const user = get(viewer, 'user');
        const userCurrency = get(user, 'profile.currency');
        const regionalCurrency = get(viewer, 'regionalInfo[0].currency');

        if (!user && !getSessionCurrency()) {
            setSessionCurrency(regionalCurrency);
        } else if (userCurrency) {
            setLocalCurrency(userCurrency);
        }

        this.props.populateCurrency(getCurrencyPreference({ user, regionalCurrency }));

        if (this.state.fragmentVariables.fetchRegionalInfo) {
            setLocalUserGeoInfo(viewer);
        }
    }
    handleTopBarMouseOver() {
        this.setState({ forceSlideIn: true }, () => {
            this.setState({
                forceSlideIn: false,
            });
        });
    }

    render() {
        const {
            disableStickyNav,
            isUserTypeTrade,
            navigation,
            tradeNavigation,
            viewer,
            isTradeNavPreview,
        } = this.props;
        const user = get(viewer, 'user') || null;
        const { fragmentVariables, forceSlideIn, hasError } = this.state;
        const {
            isClient,
            hasUserId,
            showMarketingMessages,
            userId,
            userIds,
            fetchRegionalInfo,
        } = fragmentVariables;
        const forceDisableStickyNav = hasError;
        const hasUser = !!user;
        const hasRootPendingRefetch =
            (!hasUser && hasUserId) || (fetchRegionalInfo && !get(viewer, 'regionalInfo'));
        const BottomWrapperTag = disableStickyNav || forceDisableStickyNav ? 'div' : Drawer;
        const bottomWrapperProps =
            disableStickyNav || forceDisableStickyNav ? {} : { forceSlideIn };
        const showTradeNavigation =
            (isUserTypeTrade &&
                get(viewer, 'isTradeNavEnabled') &&
                !!get(tradeNavigation, 'modules.length')) ||
            isTradeNavPreview;

        const {
            wrapperClasses,
            headerClasses,
            shadowClasses,
            bottomWrapperClasses,
        } = this.getClassnames({ forceDisableStickyNav, hasTopBarOnly: hasError });

        if (this.state.hasError) {
            return (
                <div className={wrapperClasses}>
                    <div className={headerClasses}>
                        <TopBar
                            hasError
                            disableStickyNav
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUser={hasUser}
                            hasUserId={hasUserId}
                            viewer={viewer}
                            isClient={isClient}
                            userId={userId}
                            user={user}
                        />
                    </div>
                    <div className={shadowClasses} />
                </div>
            );
        }

        return (
            <div className={wrapperClasses}>
                <div className={headerClasses}>
                    <TopBar
                        disableStickyNav={disableStickyNav}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUser={hasUser}
                        hasUserId={hasUserId}
                        viewer={viewer}
                        isClient={isClient}
                        userId={userId}
                        user={user}
                        onMouseOver={this.handleTopBarMouseOver}
                    />
                    <BottomWrapperTag className={bottomWrapperClasses} {...bottomWrapperProps}>
                        <BottomBar
                            navigation={showTradeNavigation ? tradeNavigation : navigation}
                            viewer={viewer}
                            isClient={isClient}
                            hasUserId={hasUserId}
                            userIds={userIds}
                            hasRootPendingRefetch={hasRootPendingRefetch}
                        />
                    </BottomWrapperTag>
                </div>
                <div className={shadowClasses} />
                {showMarketingMessages ? (
                    <MessageBanner
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUserId={hasUserId}
                        isClient={isClient}
                        message={null}
                        showMarketingMessages={showMarketingMessages}
                        user={user}
                        userIds={userIds}
                        viewer={viewer}
                    />
                ) : null}
            </div>
        );
    }
}

Header.propTypes = {
    disableStickyNav: bool,
    initialTrackingFired: bool,
    isClient: bool,
    isMobile: bool,
    isTradeNavPreview: bool,
    isUserTypeTrade: bool,
    navigation: object,
    tradeNavigation: object,
    setInitialTrackingFired: func,
    showMarketingMessages: bool,
    relay: object,
    updateUserTypeTrade: func,
    updateUserTypeVip: func,
    populateCurrency: func,
    updateUserState: bool,
    viewer: object,
};

const mapStateToProps = ({ header }) => ({
    isMobile: header.isMobile,
    disableStickyNav: header.disableStickyNav,
    updateUserState: header.updateUserState,
    initialTrackingFired: header.initialTrackingFired,
    isUserTypeTrade: header.isUserTypeTrade,
    isTradeNavPreview: header.isTradeNavPreview,
});

const mapDispatchToProps = dispatch => ({
    updateUserTypeTrade: () => {
        dispatch(actionCreators.setUserTypeTrade(true));
    },
    updateUserTypeVip: () => {
        dispatch(actionCreators.setUserTypeVip(true));
    },
    setInitialTrackingFired: () => {
        dispatch(actionCreators.setInitialTrackingFired());
    },
    populateCurrency: currency => dispatch(actionCreators.populateCurrency(currency)),
});

Header = createRefetchContainer(
    connect(mapStateToProps, mapDispatchToProps)(Header),
    {
        navigation: graphql`
            fragment Header_navigation on Navigation {
                ...BottomBar_navigation
            }
        `,
        tradeNavigation: graphql`
            fragment Header_tradeNavigation on Navigation {
                ...BottomBar_navigation
                modules {
                    serviceId
                }
            }
        `,
        viewer: graphql`
            fragment Header_viewer on Viewer {
                ...TopBar_viewer
                ...userTracking_viewer @relay(mask: false)
                ...userGeoInfo_regionalInfo @relay(mask: false) @include(if: $fetchRegionalInfo)
                ...BottomBar_viewer
                ... on Viewer @include(if: $hasUserId) {
                    ...chat_viewer @relay(mask: false)
                }
                ... on Viewer @include(if: $showMarketingMessages) {
                    ...MessageBanner_viewer
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...userTracking_user @relay(mask: false)
                    ...chat_user @relay(mask: false)
                    ... on User @include(if: $showMarketingMessages) {
                        ...MessageBanner_user
                    }
                    ...TopBar_user
                    isVip
                    isVerifiedTrade
                    profile {
                        currency
                    }
                }
                isTradeNavEnabled: isEnabled(key: "showTradeNavigation")
                isZipCodeResolverEnabled: isEnabled(key: "resolveIp2Zipcode")
            }
        `,
    },
    graphql`
        query HeaderRefetchQuery(
            $userId: String!
            $userIds: [String]!
            $hasUserId: Boolean!
            $showMarketingMessages: Boolean!
            $fetchMarketingMessagesTransactions: Boolean!
            $fetchRegionalInfo: Boolean!
        ) {
            viewer {
                ...Header_viewer
            }
        }
    `
);

export default Header;
